import { OfferingCategory } from "@albertine/types";
import { z } from "zod";
import { Price } from "../currency";
import { LongString, MediumLongString } from "../validate";
import { ImageAttachment } from "./Attachment";

export const BookingOption = z.object({
    id: z.uuid(),
    title: MediumLongString,
    description: LongString.optional().nullable(),
    images: z.array(ImageAttachment).optional().nullable(),
    price: Price.optional().nullable(),
    confirmed: z.boolean().optional().nullable(),
    memberPreferred: z.boolean().optional().nullable(),
});
export type BookingOption = z.infer<typeof BookingOption>;

export const BookingOptionUpdate = BookingOption.pick({
    id: true,
}).extend(
    BookingOption.omit({
        id: true,
    }).partial().shape,
);
export type BookingOptionUpdate = z.infer<typeof BookingOptionUpdate>;

export const BookingOptionsAllowedCategory = z.enum([
    OfferingCategory.enum.accommodation,
    OfferingCategory.enum.flight,
    OfferingCategory.enum.event,
]);
export type BookingOptionsAllowedCategory = z.infer<
    typeof BookingOptionsAllowedCategory
>;

export const BookingOptionsTextsMap: Record<
    BookingOptionsAllowedCategory,
    {
        title: string;
        description: string;
        optionTitle: string;
        optionNamePlaceholder: string;
        optionDescriptionPlaceholder: string;
        addButton: string;
        deleteButton: string;
    }
> = {
    accommodation: {
        title: "Room options",
        description: "Add room options when offering multiple hotel room types.",
        optionTitle: "Room details",
        optionNamePlaceholder: "Eg. Junior Suite",
        optionDescriptionPlaceholder:
            "What does the room include? Brief bullets only, no full sentences.",
        addButton: "New room option",
        deleteButton:
            "Delete room option?\n\nYou cannot restore the option after deleting. Please avoid deleting options you’ve already shared with the member.",
    },
    flight: {
        title: "Flight options",
        description:
            "Add flight options when offering alternatives for the member to choose from, eg. business or economy, morning or afternoon flight.",
        optionTitle: "Flight details",
        optionNamePlaceholder: "E.g. Business class, Morning flight",
        optionDescriptionPlaceholder:
            "What does the flight include? Brief bullets only, no full sentences.",
        addButton: "New flight option",
        deleteButton:
            "Delete flight option?\n\nYou cannot restore the option after deleting. Please avoid deleting options you’ve already shared with the member.",
    },
    event: {
        title: "Ticket options",
        description:
            "Add ticket options when offering several ticket alternatives the member can choose from.",
        optionTitle: "Ticket details",
        optionNamePlaceholder: "Eg. VIP ticket",
        optionDescriptionPlaceholder:
            "What does the ticket include? Brief bullets only, no full sentences.",
        addButton: "New ticket option",
        deleteButton:
            "Delete ticket option?\n\nYou cannot restore the option after deleting. Please avoid deleting options you’ve already shared with the member.",
    },
};
